import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

export type SitePage = Database['public']['Tables']['site_pages']['Row'];

export function useSitePages(tenantId: string | null) {
  return useQuery({
    queryKey: ['sitePages', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('site_pages')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('is_published', true)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching site pages:', error);
        throw error;
      }

      return (data || []) as SitePage[];
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });
}

export function useHomePageBlocks(tenantId: string | null) {
  return useQuery({
    queryKey: ['homePageBlocks', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      // Find the "home" page for this tenant
      let { data: homePage } = await supabase
        .from('site_pages')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('slug', 'home')
        .maybeSingle();

      // If it doesn't exist, create it transparently ONLY if authenticated (admin)
      if (!homePage) {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // If unauthenticated (public view), don't try to insert, just return empty blocks
          return [];
        }

        const { data: newPage, error } = await supabase
          .from('site_pages')
          .insert({
            tenant_id: tenantId,
            title: 'Página Inicial',
            slug: 'home',
            is_published: true,
          })
          .select('id')
          .single();
        
        if (error || !newPage) return [];
        homePage = newPage;
      }

      // Fetch blocks for the home page
      const { data: blocks, error: blocksError } = await supabase
        .from('site_page_blocks')
        .select('*')
        .eq('page_id', homePage.id)
        .order('order_index', { ascending: true });

      if (blocksError) {
        console.error('Error fetching home page blocks:', blocksError);
        return [];
      }

      return blocks || [];
    },
    enabled: !!tenantId,
  });
}
